import React, {useMemo, useState} from "react";
import {Button, Col, Container, Input, Row} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableErrorClassifier from "./components/TableErrorClassifier";
import ModalErrorClassifierKeyword from "./components/ModalErrorClassifierKeyword";
import ModalErrorClassifierGroup from "./components/ModalErrorClassifierGroup";
import {useQuery} from "@tanstack/react-query";
import {ErrorClassifierService} from "../../services/error_classifier.service";
import NoSearchResult from "../../components/Common/NoSearchResult";
import ModalErrorKeys from "./components/ErrorKeys/ModalErrorKeys";
import {IErrorClassifierGroup} from "../../store/errorClassifier/types";
import useDebounce from "../../hooks/useDebounce";
import usePermissions from "../../hooks/usePermissions";
import {Navigate} from "react-router-dom";

interface ModalState {
    isModalOpen: boolean;
    id: number | null;
}

type ErrorKeysModalState = Omit<ModalState, "id">;

const ErrorClassifier = () => {
    document.title = "Error Classifier | Dashboard";

    const {hasPermission} = usePermissions();
    if (!hasPermission("view-error-classifier")) return <Navigate to="/no-access"/>

    const [search, setSearch] = useState<string>("");
    const debouncedSearch = useDebounce(search, 250);

    const [groupModal, setGroupModal] = useState<ModalState>({isModalOpen: false, id: null});
    const [keywordModal, setKeywordModal] = useState<ModalState>({isModalOpen: false, id: null});
    const [errorKeysModal, setErrorKeysModal] = useState<ErrorKeysModalState>({isModalOpen: false});

    const errorClassifiersFiltered = (groups: IErrorClassifierGroup[]) =>
        groups.sort((a, b) => Number(a.nostatus_group) - Number(b.nostatus_group))

    const {
        data: errorClassifiers,
        isLoading,
        isError,
    } = useQuery({
        queryKey: ["keyErrorClassifierGroups"],
        queryFn: () => ErrorClassifierService.getGroupsAll(),
        select: errorClassifiersFiltered
    });

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
    }

    const handleAddGroup = () => {
        setGroupModal({isModalOpen: true, id: null});
    }

    const handleUpdateGroup = (groupId: number) => {
        setGroupModal({isModalOpen: true, id: groupId});
    }

    const handleGroupModalClose = () => {
        setGroupModal({isModalOpen: false, id: null});
    }

    const handleAddKeyword = () => {
        setKeywordModal({isModalOpen: true, id: null});
    }

    const handleUpdateKeyword = (keywordId: number) => {
        setKeywordModal({isModalOpen: true, id: keywordId});
    }

    const handleKeywordModalClose = () => {
        setKeywordModal({isModalOpen: false, id: null});
    }

    const handleErrorKeysModalOpen = () => {
        setErrorKeysModal({isModalOpen: true});
    }

    const handleErrorKeysModalClose = () => {
        setErrorKeysModal({isModalOpen: false});
    }

    const filteredErrors = useMemo(() => {
        if (!errorClassifiers) return [];

        if (!search.trim()) return errorClassifiers;

        const query = search.toLowerCase();

        return errorClassifiers.map((group) => {
            const groupMatches = group.name.toLowerCase().includes(query);
            const matchingKeywords = group.keywords.filter((kw) => kw.name.toLowerCase().includes(query));

            if (groupMatches) return group;

            if (matchingKeywords.length > 0) {
                return {
                    ...group,
                    keywords: matchingKeywords,
                };
            }

            return null;
        }).filter((group): group is IErrorClassifierGroup => group !== null);
    }, [debouncedSearch, errorClassifiers]);

    return (
        <div className="page-content">
            <Container fluid>
                {/* Render Breadcrumbs */}
                <Breadcrumbs title="Dashboard" breadcrumbItem="Error Classifier"/>

                <Row className="align-items-center">
                    <Col md={12}>
                        <div className="d-flex align-items-center justify-content-between gap-2 mb-3 flex-wrap">
                            <div className="d-flex align-items-center gap-2">
                                <Input
                                    id="search"
                                    name="search"
                                    placeholder="Search..."
                                    value={search}
                                    onChange={handleSearchChange}
                                />
                                <Button className="d-flex align-items-center gap-1 flex-shrink-0" color="secondary"
                                        onClick={handleErrorKeysModalOpen}>
                                    <i className="bx bx-pencil me-1"></i> Error Keys
                                </Button>
                            </div>

                            {hasPermission("create-error-classifier") &&
                                <div className="d-flex align-items-center gap-2">
                                    <Button className="d-flex align-items-center gap-1" color="light"
                                            onClick={handleAddGroup}>
                                        <i className="bx bx-plus me-1"></i> Add Group
                                    </Button>
                                    <Button className="d-flex align-items-center gap-1" color="secondary"
                                            onClick={handleAddKeyword}>
                                        <i className="bx bx-plus me-1"></i> Add Keyword
                                    </Button>
                                </div>}
                        </div>
                    </Col>
                </Row>

                <Row>
                    <Col>

                        {!isLoading && errorClassifiers && filteredErrors?.length > 0
                            && <TableErrorClassifier data={filteredErrors} onUpdateGroud={handleUpdateGroup}
                                                     onUpdateKeyword={handleUpdateKeyword}/>}

                        {!isLoading && filteredErrors?.length === 0 && (<NoSearchResult/>)}
                        {isLoading && (<NoSearchResult text="Loading..."/>)}
                        {isError && (<NoSearchResult/>)}
                    </Col>
                </Row>

                {groupModal.isModalOpen &&
                    <ModalErrorClassifierGroup isOpen={groupModal.isModalOpen}
                                               groupId={groupModal.id}
                                               onClose={handleGroupModalClose}/>}

                {keywordModal.isModalOpen &&
                    <ModalErrorClassifierKeyword isOpen={keywordModal.isModalOpen}
                                                 keywordId={keywordModal.id}
                                                 onClose={handleKeywordModalClose}/>}

                {errorKeysModal.isModalOpen &&
                    <ModalErrorKeys isOpen={errorKeysModal.isModalOpen}
                                    onClose={handleErrorKeysModalClose}/>}
            </Container>
        </div>
    )
}

export default ErrorClassifier;